import { Box, Flex, Text } from '@chakra-ui/react';
import type { ReactNode } from 'react';

import type { BizyUiBlockAction } from '../../types/ui-blocks';
import { UiBlockActionBar } from './ui-block-action-bar';

/**
 * Shared shell for the user-admin draft/confirm cards: a title, an optional
 * target subtitle, the card body, and the standard {@link UiBlockActionBar}
 * footer (Confirm / Cancel).
 */
export function UserAdminCard({
	title,
	subtitle,
	actions,
	isReadOnly = false,
	fontSize = 'sm',
	children,
}: {
	title: string;
	subtitle?: string;
	actions: readonly BizyUiBlockAction[];
	isReadOnly?: boolean;
	fontSize?: 'sm' | 'md';
	children?: ReactNode;
}) {
	return (
		<Box my={2} px={3} py={3} bg="bg.surface" border="1px solid" borderColor="border.default" borderRadius="md">
			<Text fontSize={fontSize} fontWeight="semibold" color="text.default">
				{title}
			</Text>
			{subtitle && (
				<Text fontSize="xs" color="text.softer" mt={0.5}>
					{subtitle}
				</Text>
			)}
			{children && <Box mt={2}>{children}</Box>}
			<UiBlockActionBar actions={actions} areActionsDisabled={isReadOnly} oneShot />
		</Box>
	);
}

export function UserAdminFieldRow({ label, value, fontSize = 'sm' }: { label: string; value: ReactNode; fontSize?: 'sm' | 'md' }) {
	return (
		<Flex fontSize={fontSize} gap={2} lineHeight="tall" align="baseline">
			<Text as="span" fontSize="inherit" color="text.softer" flexShrink={0}>
				{label}
			</Text>
			<Text as="span" fontSize="inherit" color="text.default" fontWeight="medium" minW={0} isTruncated title={typeof value === 'string' ? value : undefined}>
				{value}
			</Text>
		</Flex>
	);
}

export function UserAdminChangeRow({ label, before, after, fontSize = 'sm' }: { label: string; before: ReactNode; after: ReactNode; fontSize?: 'sm' | 'md' }) {
	return (
		<Flex fontSize={fontSize} gap={2} lineHeight="tall" align="baseline" flexWrap="wrap">
			<Text as="span" fontSize="inherit" color="text.softer" flexShrink={0}>
				{label}
			</Text>
			<Text
				as="span"
				fontSize="inherit"
				color="text.softer"
				textDecoration="line-through"
				minW={0}
				isTruncated
				title={typeof before === 'string' ? before : undefined}
			>
				{before}
			</Text>
			<Text as="span" fontSize="inherit" color="text.softer" aria-hidden>
				{'\u2192'}
			</Text>
			<Text as="span" fontSize="inherit" color="text.default" fontWeight="medium" minW={0} isTruncated title={typeof after === 'string' ? after : undefined}>
				{after}
			</Text>
		</Flex>
	);
}

/**
 * Humanize a raw change-field token (`company_admin`, `preferred_first_name`)
 * into a Title Case label for display.
 */
export function humanizeFieldToken(token: string): string {
	return token
		.replace(/[_-]+/g, ' ')
		.trim()
		.replace(/\b\w/g, (c) => c.toUpperCase());
}
